// File: e1c/src/components/content_editor/StandardizedAssessmentBlock.js
// @author: Sunil Jain
// Description: A new editor block for selecting and configuring a standardized assessment.

import React, { useCallback, useMemo } from 'react';
import {
    Paper, TextField, IconButton, Box, Typography,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// This list will eventually be populated from an API or a more robust constants file.
const AVAILABLE_ASSESSMENTS = [
    { key: 'BDI_II', name: 'Beck Depression Inventory-II (BDI-II)' },
    { key: 'BAI', name: 'Beck Anxiety Inventory (BAI)' },
    { key: 'PCL_5', name: 'PTSD Checklist for DSM-5 (PCL-5)' },
    { key: 'WASI_II_VOCABULARY', name: 'WASI-II: Vocabulary' },
    { key: 'WASI_II_BLOCK_DESIGN', name: 'WASI-II: Block Design' },
    { key: 'CVLT_II', name: 'California Verbal Learning Test-II (CVLT-II)' },
];

const StandardizedAssessmentBlock = React.memo(({ id, question, options = {}, onChange, onDelete }) => {

    const memoizedOptions = useMemo(() => ({
        assessment_key: options.assessment_key || '',
    }), [options.assessment_key]);

    const handleQuestionChange = useCallback((event) => {
        if (onChange) {
            onChange({ question: event.target.value });
        }
    }, [onChange]);

    const handleOptionChange = useCallback((optionName, value) => {
        if (onChange) {
            onChange({ options: { [optionName]: value } });
        }
    }, [onChange]);

    const handleDeleteClick = useCallback(() => {
        if (onDelete) { onDelete(id); }
    }, [id, onDelete]);

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, position: 'relative', backgroundColor: 'primary.lightest', '&:hover .delete-button': { opacity: 1 } }}>
            <IconButton
                className="delete-button" aria-label="delete block" onClick={handleDeleteClick} size="small"
                sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.500', opacity: 0.5, transition: 'opacity 0.2s', '&:hover': { opacity: 1, color: 'error.main' } }}
            >
                <DeleteOutlineIcon fontSize="small"/>
            </IconButton>

            <TextField
                fullWidth variant="outlined" size="small"
                label={`Assessment Block Title (ID: ${id})`}
                multiline
                value={question || ''}
                onChange={handleQuestionChange}
                placeholder="Enter a title for this assessment..."
                sx={{ mb: 2 }}
            />

            <FormControl fullWidth variant="outlined">
                <InputLabel id={`assessment-select-label-${id}`}>Select Standardized Assessment</InputLabel>
                <Select
                    labelId={`assessment-select-label-${id}`}
                    value={memoizedOptions.assessment_key}
                    onChange={(e) => handleOptionChange('assessment_key', e.target.value)}
                    label="Select Standardized Assessment"
                >
                    <MenuItem value=""><em>-- Please select an assessment --</em></MenuItem>
                    {AVAILABLE_ASSESSMENTS.map(assessment => (
                        <MenuItem key={assessment.key} value={assessment.key}>
                            {assessment.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Paper>
    );
});

export default StandardizedAssessmentBlock;